import React from 'react';
import { ExternalLink, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import PixelBackground from '../components/PixelBackground';
import Seo from '../components/Seo';
import Display from '../components/typography/Display';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { HeroSection, Container, Section } from '../components/ui/Section';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { withLanguagePrefix } from '@/lib/locale';

interface ProjectCardProps {
  title: string;
  description: string;
  tags: string[];
  result: string;
  challenge: string;
  solution: string;
  metrics?: string[];
  websiteUrl?: string;
  isExpanded: boolean;
  onToggle: () => void;
  comingSoon?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  tags,
  result,
  challenge,
  solution,
  metrics = [],
  websiteUrl,
  isExpanded,
  onToggle,
  comingSoon = false
}) => {
  const { t } = useLanguage();
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isExpanded && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isExpanded]);

  return (
    <div ref={cardRef} className={`${isExpanded ? 'lg:col-span-2' : ''}`}>
      <Card className={`h-full ${isExpanded ? 'lg:grid lg:grid-cols-2 lg:gap-8' : ''}`}>
        {/* Left Side - Brief Info + Challenge & Solution */}
        <div className={isExpanded ? '' : 'flex flex-col h-full'}>
          <div className="flex-grow">
            <h3 className="text-xl font-bold text-white mb-3">
              {title}
            </h3>
            <p className="text-white/70 text-base leading-relaxed mb-4">{description}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-white/5 border border-white/10 text-white/80 py-1 px-3 rounded-full text-xs hover:bg-white/10 transition-all duration-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          <div className="flex flex-col gap-3 mt-4">
            {comingSoon ? (
              <span className="bg-white/5 border border-white/10 text-white/60 py-3 px-6 rounded-lg font-medium flex items-center justify-center">
                {t('projects.comingSoon')}
              </span>
            ) : websiteUrl ? (
              <Button
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
                size="base"
                className="w-full justify-center gap-2"
              >
                {t('projects.buttons.more')}
                <ExternalLink className="h-4 w-4" />
              </Button>
            ) : null}

            <Button
              as="button"
              onClick={onToggle}
              variant={isExpanded ? 'secondary' : 'ghost'}
              size="sm"
              className="w-full justify-center"
            >
              {isExpanded ? (
                <span className="flex items-center gap-2">
                  <X className="h-4 w-4" />
                  {t('projects.buttons.less')}
                </span>
              ) : t('projects.buttons.details')}
            </Button>
          </div>

          {/* Challenge & Solution on left side when expanded */}
          {isExpanded && (
            <div className="space-y-4 mt-6 lg:mt-6">
              <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                <h4 className="text-white font-semibold text-base mb-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-brand-highlight/70" />
                  {t('projects.challenge')}
                </h4>
                <p className="text-white/70 text-sm leading-relaxed">{challenge}</p>
              </div>
              
              <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                <h4 className="text-white font-semibold text-base mb-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-brand-highlight/70" />
                  {t('projects.solution')}
                </h4>
                <p className="text-white/70 text-sm leading-relaxed">{solution}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {isExpanded && (
          <div className="lg:pl-6 lg:border-l lg:border-white/10 mt-6 lg:mt-0 flex flex-col">
            {/* Placeholder Image Area */}
            <div className="rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10 p-6 mb-6 flex-grow min-h-[200px]">
              <div className="flex items-center justify-center h-full text-white/30 text-sm">
                <div className="text-center">
                  <p className="mb-2">Project Preview</p>
                  <p className="text-xs opacity-60">Image placeholder</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                <h4 className="text-white font-semibold text-base mb-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-brand-highlight/70" />
                  {t('projects.results')}
                </h4>
                <p className="text-white/70 text-sm leading-relaxed mb-4">{result}</p>
                {metrics.length > 0 && (
                  <div className="grid grid-cols-1 gap-2">
                    {metrics.map((metric, i) => (
                      <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-sm text-white/80">{metric}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const Portfolio: React.FC = () => {
  const { t, language } = useLanguage();
  const locale = language === 'en' ? 'en' : 'el';
  const meta = getPageMeta('portfolio', undefined, locale);
  const canonicalPath = withLanguagePrefix('/projects', locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks('/projects', locale);
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Localized copy for the portfolio page
  const portfolioCopy = {
    en: {
      eyebrow: 'PORTFOLIO',
      title: 'Selected Projects',
      subhead: 'Explore our recent work across websites, e-commerce, branding, and digital strategy. Each project reflects our commitment to quality and measurable results.',
    },
    el: {
      eyebrow: 'PORTFOLIO',
      title: 'Επιλεγμένα Έργα',
      subhead: 'Ανακαλύψτε τη πρόσφατη δουλειά μας σε ιστοσελίδες, e-commerce, branding και ψηφιακή στρατηγική. Κάθε έργο αντικατοπτρίζει τη δέσμευσή μας για ποιότητα και μετρήσιμα αποτελέσματα.',
    },
  };

  const copy = portfolioCopy[locale];
  
  const projects = [
    {
      title: t('projects.pieriastrom.title'),
      description: t('projects.pieriastrom.description'),
      tags: [
        t('projects.pieriastrom.tags.ecommerce'),
        t('projects.pieriastrom.tags.wordpress'),
        t('projects.pieriastrom.tags.woocomerce'),
        t('projects.pieriastrom.tags.custom')
      ],
      result: t('projects.pieriastrom.result'),
      challenge: t('projects.pieriastrom.challenge'),
      solution: t('projects.pieriastrom.solution'),
      metrics: [
        t('projects.pieriastrom.metrics.1'),
        t('projects.pieriastrom.metrics.2'),
        t('projects.pieriastrom.metrics.3'),
        t('projects.pieriastrom.metrics.4'),
        t('projects.pieriastrom.metrics.5'),
        t('projects.pieriastrom.metrics.6')
      ],
      websiteUrl: "https://shop.pieriastrom.gr",
      comingSoon: true
    },
    {
      title: t('projects.antonistagiannis.title'),
      description: t('projects.antonistagiannis.description'),
      tags: [
        t('projects.antonistagiannis.tags.portfolio'),
        t('projects.antonistagiannis.tags.engineering'),
        t('projects.antonistagiannis.tags.wordpress'),
        t('projects.antonistagiannis.tags.seo')
      ],
      result: t('projects.antonistagiannis.result'),
      challenge: t('projects.antonistagiannis.challenge'),
      solution: t('projects.antonistagiannis.solution'),
      metrics: [
        t('projects.antonistagiannis.metrics.1'),
        t('projects.antonistagiannis.metrics.2'),
        t('projects.antonistagiannis.metrics.3')
      ],
      websiteUrl: "https://antonistagiannis.gr",
      comingSoon: true
    },
    {
      title: t('projects.invoicedirect.title'),
      description: t('projects.invoicedirect.description'),
      tags: [t('projects.invoicedirect.tags.saas'), t('projects.invoicedirect.tags.branding'), t('projects.invoicedirect.tags.webapp')],
      result: t('projects.invoicedirect.result'),
      challenge: t('projects.invoicedirect.challenge'),
      solution: t('projects.invoicedirect.solution'),
      metrics: [
        t('projects.invoicedirect.metrics.1'),
        t('projects.invoicedirect.metrics.2'),
        t('projects.invoicedirect.metrics.3')
      ],
      websiteUrl: "https://invoice.direct"
    },
    {
      title: t('projects.tradesignalx.title'),
      description: t('projects.tradesignalx.description'),
      tags: [t('projects.tradesignalx.tags.website'), t('projects.tradesignalx.tags.frontend'), t('projects.tradesignalx.tags.performance')],
      result: t('projects.tradesignalx.result'),
      challenge: t('projects.tradesignalx.challenge'),
      solution: t('projects.tradesignalx.solution'),
      metrics: [
        t('projects.tradesignalx.metrics.1'),
        t('projects.tradesignalx.metrics.2'),
        t('projects.tradesignalx.metrics.3')
      ],
      websiteUrl: "https://tradesignalx.com"
    },
    {
      title: t('projects.lensory.title'),
      description: t('projects.lensory.description'),
      tags: [t('projects.lensory.tags.website'), t('projects.lensory.tags.branding'), t('projects.lensory.tags.seo')],
      result: t('projects.lensory.result'),
      challenge: t('projects.lensory.challenge'),
      solution: t('projects.lensory.solution'),
      metrics: [
        t('projects.lensory.metrics.1'),
        t('projects.lensory.metrics.2'),
        t('projects.lensory.metrics.3')
      ],
      websiteUrl: "https://lensory.co"
    },
    {
      title: t('projects.gkoutzioulis.title'),
      description: t('projects.gkoutzioulis.description'),
      tags: [t('projects.gkoutzioulis.tags.portfolio'), t('projects.gkoutzioulis.tags.branding'), t('projects.gkoutzioulis.tags.responsive')],
      result: t('projects.gkoutzioulis.result'),
      challenge: t('projects.gkoutzioulis.challenge'),
      solution: t('projects.gkoutzioulis.solution'),
      metrics: [
        t('projects.gkoutzioulis.metrics.1'),
        t('projects.gkoutzioulis.metrics.2'),
        t('projects.gkoutzioulis.metrics.3')
      ],
      websiteUrl: "https://ioannis-gkoutzioulis.com"
    },
    {
      title: t('projects.voltbuild.title'),
      description: t('projects.voltbuild.description'),
      tags: [t('projects.voltbuild.tags.website'), t('projects.voltbuild.tags.eshop'), t('projects.voltbuild.tags.seo'), t('projects.voltbuild.tags.strategy')],
      result: t('projects.voltbuild.result'),
      challenge: t('projects.voltbuild.challenge'),
      solution: t('projects.voltbuild.solution'),
      metrics: [
        t('projects.voltbuild.metrics.1'),
        t('projects.voltbuild.metrics.2'),
        t('projects.voltbuild.metrics.3')
      ],
      websiteUrl: "https://voltbuild.gr"
    },
    {
      title: t('projects.naterra.title'),
      description: t('projects.naterra.description'),
      tags: [t('projects.naterra.tags.branding'), t('projects.naterra.tags.website'), t('projects.naterra.tags.marketing')],
      result: t('projects.naterra.result'),
      challenge: t('projects.naterra.challenge'),
      solution: t('projects.naterra.solution'),
      metrics: [
        t('projects.naterra.metrics.1'),
        t('projects.naterra.metrics.2'),
        t('projects.naterra.metrics.3')
      ],
      websiteUrl: "https://naterra.uk"
    },
    {
      title: t('projects.kts.title'),
      description: t('projects.kts.description'),
      tags: [t('projects.kts.tags.website'), t('projects.kts.tags.rebranding'), t('projects.kts.tags.digital'), t('projects.kts.tags.social')],
      result: t('projects.kts.result'),
      challenge: t('projects.kts.challenge'),
      solution: t('projects.kts.solution'),
      metrics: [
        t('projects.kts.metrics.1'),
        t('projects.kts.metrics.2'),
        t('projects.kts.metrics.3')
      ],
      websiteUrl: "https://ktswoodart.com"
    },
    {
      title: t('projects.tsiouchas.title'),
      description: t('projects.tsiouchas.description'),
      tags: [t('projects.tsiouchas.tags.wordpress'), t('projects.tsiouchas.tags.onepage'), t('projects.tsiouchas.tags.lead'), t('projects.tsiouchas.tags.automation'), t('projects.tsiouchas.tags.plugin')],
      result: t('projects.tsiouchas.result'),
      challenge: t('projects.tsiouchas.challenge'),
      solution: t('projects.tsiouchas.solution'),
      metrics: [
        t('projects.tsiouchas.metrics.1'),
        t('projects.tsiouchas.metrics.2'),
        t('projects.tsiouchas.metrics.3')
      ],
      websiteUrl: "https://tsiouchas.com"
    },
    {
      title: t('projects.sidiropoulos.title'),
      description: t('projects.sidiropoulos.description'),
      tags: [t('projects.sidiropoulos.tags.website'), t('projects.sidiropoulos.tags.medical'), t('projects.sidiropoulos.tags.seo')],
      result: t('projects.sidiropoulos.result'),
      challenge: t('projects.sidiropoulos.challenge'),
      solution: t('projects.sidiropoulos.solution'),
      metrics: [
        t('projects.sidiropoulos.metrics.1'),
        t('projects.sidiropoulos.metrics.2'),
        t('projects.sidiropoulos.metrics.3')
      ],
      websiteUrl: "https://sidiropoulos-urology.gr"
    },
    {
      title: t('projects.underground.title'),
      description: t('projects.underground.description'),
      tags: [t('projects.underground.tags.website'), t('projects.underground.tags.branding'), t('projects.underground.tags.marketing360'), t('projects.underground.tags.products')],
      result: t('projects.underground.result'),
      challenge: t('projects.underground.challenge'),
      solution: t('projects.underground.solution'),
      metrics: [
        t('projects.underground.metrics.1'),
        t('projects.underground.metrics.2'),
        t('projects.underground.metrics.3'),
        t('projects.underground.metrics.4')
      ],
      websiteUrl: "https://undergroundtalent.co.uk"
    },
  ];

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      
      <div className="min-h-screen bg-pp-black text-white relative">
        <PixelBackground />
        <Header variant="simple" />

        {/* Hero Section - Following Homepage Pattern */}
        <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
          <div className="absolute inset-0 geometric-pattern" />
          <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" />
          
          <Container className="relative">
            <div className="text-center max-w-4xl mx-auto space-y-6 md:space-y-8">
              {/* Eyebrow */}
              <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                {copy.eyebrow}
              </p>
              
              {/* H1 Title */}
              <Display 
                variant="h1" 
                as="h1"
                className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
              >
                {copy.title}
              </Display>
              
              {/* Subhead */}
              <Display
                variant="subhead"
                as="p"
                className="px-2 text-white/70 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed"
              >
                {copy.subhead}
              </Display>
            </div>
          </Container>
        </HeroSection>

        {/* Projects Grid Section */}
        <Section className="py-16 md:py-24">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 grid-flow-dense">
              {projects.map((project, index) => (
                <ProjectCard
                  key={index}
                  title={project.title}
                  description={project.description}
                  tags={project.tags}
                  result={project.result}
                  challenge={project.challenge}
                  solution={project.solution}
                  metrics={project.metrics}
                  websiteUrl={project.websiteUrl}
                  isExpanded={expandedIndex === index}
                  onToggle={() => handleToggle(index)}
                  comingSoon={project.comingSoon}
                />
              ))}
            </div>
          </Container>
        </Section>

        {/* CTA Section - Following Homepage Pattern */}
        <Section className="pb-16 md:pb-24">
          <Container>
            <div className="rounded-[32px] border border-white/15 bg-black/70 backdrop-blur-xl p-8 md:p-12 shadow-glass">
              <div className="text-center space-y-6">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                  {locale === 'en' ? 'Collaboration' : 'Συνεργασία'}
                </p>
                <Display
                  variant="h1"
                  as="h2"
                  className="mx-auto max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                >
                  {locale === 'en' 
                    ? 'Have a project in mind?' 
                    : 'Έχετε ένα έργο στο μυαλό σας;'}
                </Display>
                <p className="mx-auto max-w-2xl text-white/70">
                  {locale === 'en'
                    ? 'Let\'s discuss how we can help bring your digital vision to life with our expertise in web design, development, and digital strategy.'
                    : 'Ας συζητήσουμε πώς μπορούμε να βοηθήσουμε να ζωντανέψει η ψηφιακή σας όραση με την τεχνογνωσία μας στο σχεδιασμό ιστοσελίδων, την ανάπτυξη και τη ψηφιακή στρατηγική.'}
                </p>
                
                <div className="flex flex-col gap-4 text-center sm:flex-row sm:justify-center pt-4">
                  <Button
                    as="button"
                    onClick={() => window.navigateToConsultation?.()}
                    trackCta={{
                      text: locale === 'en' ? 'Book a free consultation' : 'Κλείστε δωρεάν συμβουλευτική',
                      section: 'portfolio_cta',
                      variant: 'primary'
                    }}
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {locale === 'en' ? 'Book a free consultation' : 'Κλείστε δωρεάν συμβουλευτική'}
                  </Button>
                  <Button
                    as="a"
                    href={withLanguagePrefix('/services', locale)}
                    trackCta={{
                      text: locale === 'en' ? 'View our services' : 'Δείτε τις υπηρεσίες μας',
                      section: 'portfolio_cta',
                      variant: 'secondary'
                    }}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {locale === 'en' ? 'View our services' : 'Δείτε τις υπηρεσίες μας'}
                  </Button>
                </div>
              </div>
            </div>
          </Container>
        </Section>

        <Footer variant="compact" />
      </div>
    </>
  );
};

export default Portfolio;
